import { DateTime, Duration, DurationUnit, Interval } from 'luxon';

import { parseOptionalLuxonDateTimeFromIsoString } from './parse-optional-luxon-datetime-from-iso-string.function';

/**
 * Calculates the duration between two dates in specified units. If no units are provided, it defaults to days.
 * @param dateFrom - The start date.
 * @param dateTo - The end date.
 * @param options - The options to calculate the duration.
 * @returns The duration between the two dates in the specified units. If the duration is not long enough
 * to be represented in the specified units, they will be omitted.
 * @example
 * toDuration('2021-01-01', '2021-01-31', { units: ['days'] }); // Duration { days: 30 }
 * toDuration('2021-01-01', '2021-01-31', { units: ['months'] }); // Duration { months: 1 }
 * toDuration('2021-01-01', '2021-01-31', { units: ['days', 'months'] }); // Duration { days: 30, months: 1 }
 * toDuration('2021-01-01', '2021-01-31', { units: ['days', 'months', 'years'] }); // Duration { days: 30, months: 1, years: 0 }
 * toDuration('2021-01-01', '2021-01-31', { units: ['days', 'months', 'years', 'hours'] }); // Duration { days: 30, months: 1, years: 0, hours: 0 }
 */
export function toDuration(
	dateFrom: Date | DateTime | string | null | undefined,
	dateTo: Date | DateTime | string | null | undefined,
	options?: {
		units?: DurationUnit[] | undefined;
	},
): Duration | null {
	if (typeof dateFrom === 'string') {
		const parsedFrom = parseOptionalLuxonDateTimeFromIsoString(dateFrom);

		if (!parsedFrom) {
			return null;
		}

		dateFrom = parsedFrom;
	}

	if (typeof dateTo === 'string') {
		const parsedTo = parseOptionalLuxonDateTimeFromIsoString(dateTo);

		if (!parsedTo) {
			return null;
		}

		dateTo = parsedTo;
	}

	// Check if dates are valid
	if (!dateFrom || !dateTo || dateFrom > dateTo) {
		return null;
	}

	// Create an interval between the two dates
	const interval = Interval.fromDateTimes(dateFrom, dateTo);

	// Return the duration
	return interval.toDuration(options?.units || ['days']);
}
